import React, { createContext, useContext, useRef } from 'react';

type Callback<T = any> = (data: T) => void;

type PubSub = {
  subscribe: <T = any>(event: string, callback: Callback<T>) => () => void;
  publish: <T = any>(event: string, data?: T | undefined) => void;
};

const PubSubContext = createContext<PubSub | null>(null);

export const PubSubProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const listeners = useRef<Record<string, Set<Callback>>>({});

  const subscribe: PubSub['subscribe'] = (event, callback) => {
    if (!listeners.current[event]) {
      listeners.current[event] = new Set();
    }
    listeners.current[event].add(callback);

    return () => {
      listeners.current[event].delete(callback);
      if (listeners.current[event].size === 0) {
        delete listeners.current[event];
      }
    };
  };

  const publish: PubSub['publish'] = (event, data?) => {
    listeners.current[event]?.forEach((cb) => cb(data));
  };

  return (
    <PubSubContext.Provider value={{ subscribe, publish }}>
      {children}
    </PubSubContext.Provider>
  );
};

export const usePubSub = (): PubSub => {
  const ctx = useContext(PubSubContext);
  if (!ctx) {
    throw new Error('usePubSub must be used within a PubSubProvider');
  }
  return ctx;
};
